import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { api } from '@/services/api'
import { LibraryMapper } from '@/services/mappers/libraryMapper'
import { Modal } from '@/components/common'
import { LexicalMarkdownEditor } from '@/components/editor/LexicalMarkdownEditor'
import { AttributionModal } from '@/components/attribution'

export const LibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'draft' | 'published'>('all')
  const [search, setSearch] = useState('')
  const [searchInputReadOnly, setSearchInputReadOnly] = useState(true)
  const [showTargetPromptModal, setShowTargetPromptModal] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editingSelectedPromptIds, setEditingSelectedPromptIds] = useState<string[]>([])
  const [expandedTargetPrompts, setExpandedTargetPrompts] = useState<Record<string, boolean>>({})
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [publishingItemId, setPublishingItemId] = useState<string | null>(null)
  const [publishingItem, setPublishingItem] = useState<any | null>(null)
  const [publishingUrls, setPublishingUrls] = useState<Record<string, string>>({})

  // Attribution integration state
  const [showAttributionModal, setShowAttributionModal] = useState(false)
  const [attributionConnected, setAttributionConnected] = useState(false)
  const [trafficDataMap, setTrafficDataMap] = useState<Record<string, number>>({})
  const [loadingTraffic, setLoadingTraffic] = useState<Record<string, boolean>>({})

  const contentLibrary = useAppStore((state) => state.contentLibrary)
  const fetchLibrary = useAppStore((state) => state.fetchLibrary)
  const upsertLibraryItem = useAppStore((state) => state.upsertLibraryItem)
  const prompts = useAppStore((state) => state.prompts)
  const fetchPrompts = useAppStore((state) => state.fetchPrompts)

  useEffect(() => {
    fetchLibrary()
    fetchPrompts()
    checkAttributionCredential()
  }, [fetchLibrary, fetchPrompts])

  // Check if attribution credential exists
  const checkAttributionCredential = async () => {
    try {
      const resp = await api.attribution.getCredentialExists()
      setAttributionConnected(resp.exists)
    } catch (e) {
      console.error('Failed to check attribution credential:', e)
    }
  }

  // Fetch traffic data for a single item
  const fetchTrafficData = useCallback(async (itemId: string) => {
    if (!attributionConnected) return
    if (loadingTraffic[itemId]) return

    const item = contentLibrary.find((x) => x.id === itemId)
    if (!item) return

    // Get published URLs from all platforms
    const urls: string[] = []
    for (const [, data] of Object.entries(item.platforms || {})) {
      const pd = data as any
      if (pd.publishedUrl && pd.publishedUrl.trim()) {
        urls.push(pd.publishedUrl.trim())
      }
    }

    if (urls.length === 0) return

    setLoadingTraffic((prev) => ({ ...prev, [itemId]: true }))
    try {
      const resp = await api.attribution.getTrafficSessionsByUrls(urls)
      if (resp.success && resp.data) {
        // Calculate total sessions across all URLs
        const totalSessions = resp.data.reduce((sum, item) => sum + item.session_count, 0)
        setTrafficDataMap((prev) => ({ ...prev, [itemId]: totalSessions }))
      }
    } catch (e) {
      console.error('Failed to fetch traffic data:', e)
    } finally {
      setLoadingTraffic((prev) => ({ ...prev, [itemId]: false }))
    }
  }, [attributionConnected, contentLibrary, loadingTraffic])

  // Fetch traffic data for all published items when attribution connects
  useEffect(() => {
    if (attributionConnected) {
      const publishedItems = contentLibrary.filter((item) => item.status === 'published')
      publishedItems.forEach((item) => {
        fetchTrafficData(item.id)
      })
    } else {
      setTrafficDataMap({})
    }
  }, [attributionConnected, contentLibrary, fetchTrafficData])

  // Open attribution modal
  const openAttributionModal = () => {
    setShowAttributionModal(true)
  }

  const filteredItems = contentLibrary.filter((item) => {
    const isGenerating = item.generationStatus === 'pending' || item.generationStatus === 'processing'
    const matchesFilter = filter === 'all'
      ? true
      : filter === 'in_progress'
        ? isGenerating
        : filter === 'draft'
          ? item.status === 'draft' && !isGenerating
          : item.status === filter
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const openPublishModal = async (contentId: string) => {
    try {
      const resp = await api.library.getById(contentId)
      if (!resp.success) return
      const item = LibraryMapper.toContentItem(resp.data)
      const urls: Record<string, string> = {}
      for (const [platform, data] of Object.entries(item.platforms || {})) {
        const d: any = data
        urls[platform] = (d.publishedUrl as string) || ''
      }
      setPublishingItemId(contentId)
      setPublishingItem(item)
      setPublishingUrls(urls)
      setShowPublishModal(true)
    } catch (e) {
      console.error('Failed to open publish modal:', e)
    }
  }

  const handlePublish = async (contentId: string) => {
    await openPublishModal(contentId)
  }

  const handleUnpublish = async (contentId: string) => {
    try {
      const response = await api.library.updateStatus(contentId, 'draft')
      if (response.success) {
        upsertLibraryItem(LibraryMapper.toContentItem(response.data))
      }
    } catch (error) {
      console.error('Failed to unpublish:', error)
    }
  }

  const handleSavePublishLinks = async () => {
    if (!publishingItemId) return
    const hasAnyUrl = Object.values(publishingUrls).some(url => url.trim())
    if (!hasAnyUrl) return

    let last: any = null
    try {
      // Save URLs first
      for (const [platform, url] of Object.entries(publishingUrls)) {
        const res = await api.library.updatePlatformPublishedUrl(
          publishingItemId,
          platform,
          url.trim() ? url.trim() : null
        )
        if (res?.success) last = res
      }

      // Mark as published only after URLs are saved
      const statusResp = await api.library.updateStatus(publishingItemId, 'published')
      if (statusResp.success) {
        const mapped = LibraryMapper.toContentItem(statusResp.data)
        upsertLibraryItem(mapped)
        setPublishingItem(mapped)
      } else if (last?.success && last.data) {
        const mapped = LibraryMapper.toContentItem(last.data)
        upsertLibraryItem(mapped)
        setPublishingItem(mapped)
      }

      // Fetch traffic data after saving published URLs
      if (attributionConnected) {
        fetchTrafficData(publishingItemId)
      }

      setShowPublishModal(false)
      setPublishingItemId(null)
      setPublishingItem(null)
      setPublishingUrls({})
    } catch (error) {
      console.error('Failed to save publish links:', error)
    }
  }

  const openTargetPromptModal = (contentId: string) => {
    const item = contentLibrary.find((x) => x.id === contentId)
    if (!item) return
    setEditingItemId(item.id)
    setEditingSelectedPromptIds(item.promptIds || [])
    setShowTargetPromptModal(true)
  }

  const handleSaveTargetPrompts = async () => {
    if (!editingItemId) return
    const selected = prompts.filter((p) => editingSelectedPromptIds.includes(p.id))
    const targetQueries = selected.map((p) => p.text).filter(Boolean)
    const targetQuery = targetQueries[0] || (contentLibrary.find((x) => x.id === editingItemId)?.title ?? '')

    try {
      const response = await api.library.update(editingItemId, {
        prompt_ids: editingSelectedPromptIds,
        target_queries: targetQueries,
        target_query: targetQuery,
      })
      if (response.success) {
        upsertLibraryItem(LibraryMapper.toContentItem(response.data))
        setShowTargetPromptModal(false)
        setEditingItemId(null)
        setEditingSelectedPromptIds([])
      }
    } catch (error) {
      console.error('Failed to update target prompts:', error)
    }
  }

  return (
    <>
      <div className="main-header">
        <h1 className="main-title">Content Monitor</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn btn-sm ${attributionConnected ? 'btn-success' : 'btn-secondary'}`}
            onClick={openAttributionModal}
            title={attributionConnected ? 'Attribution service connected' : 'Configure attribution integration'}
          >
            <svg className="icon" style={{ width: '14px', height: '14px', marginRight: '6px' }}>
              <use href="#icon-link"></use>
            </svg>
            {attributionConnected ? 'Attribution Connected' : 'Attribution'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/app/content')}>
            <svg className="icon" style={{ width: '14px', height: '14px', marginRight: '6px' }}>
              <use href="#icon-plus"></use>
            </svg>
            Create New
          </button>
        </div>
      </div>
      <div className="main-body">
        {/* Filters */}
        <div className="library-toolbar">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-tab ${filter === 'in_progress' ? 'active' : ''}`}
              onClick={() => setFilter('in_progress')}
            >
              In Progress
            </button>
            <button
              className={`filter-tab ${filter === 'draft' ? 'active' : ''}`}
              onClick={() => setFilter('draft')}
            >
              Drafts
            </button>
            <button
              className={`filter-tab ${filter === 'published' ? 'active' : ''}`}
              onClick={() => setFilter('published')}
            >
              Published
            </button>
          </div>
          <div className="filter-search">
            <svg className="icon" style={{ width: '16px', height: '16px', stroke: 'var(--text-muted)' }}>
              <use href="#icon-search"></use>
            </svg>
            <input
              type="text"
              name="q"
              placeholder="Type to filter..."
              className="filter-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => {
                if (searchInputReadOnly) {
                  setTimeout(() => setSearchInputReadOnly(false), 0)
                }
              }}
              readOnly={searchInputReadOnly}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Table Header */}
        {filteredItems.length > 0 && (
          <div className="library-table-header">
            <div>Content</div>
            <div>Target Prompt</div>
            <div>Platforms</div>
            <div>Traffic</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
        )}

        {/* Library List */}
        <div className="library-list">
          {filteredItems.length === 0 ? (
            <div className="library-empty" id="library-empty">
              <svg className="icon" style={{ width: '48px', height: '48px', stroke: 'var(--border)', marginBottom: '16px' }}>
                <use href="#icon-file"></use>
              </svg>
              <h3>No content yet</h3>
              <p>Create your first piece of GEO-optimized content</p>
              <button className="btn btn-primary btn-sm" style={{ marginTop: '16px' }} onClick={() => navigate('/app/content')}>
                Get Started
              </button>
            </div>
          ) : (
            filteredItems.map((item) => {
              const isGenerating = item.generationStatus === 'pending' || item.generationStatus === 'processing'
              const statusClass = isGenerating ? 'generating' : item.status
              const statusText = isGenerating
                ? 'Generating'
                : item.status === 'draft'
                  ? 'Draft'
                  : item.status === 'published'
                    ? 'Published'
                    : 'Scheduled'
              const trafficDisplay = trafficDataMap[item.id] !== undefined
                ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg className="icon" style={{ width: '14px', height: '14px', color: 'var(--success)' }}>
                      <use href="#icon-trending-up"></use>
                    </svg>
                    <span>{trafficDataMap[item.id].toLocaleString()}</span>
                  </div>
                )
                : loadingTraffic[item.id]
                  ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                      <svg className="icon spinning" style={{ width: '14px', height: '14px' }}>
                        <use href="#icon-loading"></use>
                      </svg>
                      <span style={{ fontSize: '12px' }}>Loading...</span>
                    </div>
                  )
                  : (
                    <button className="integrate-btn" onClick={(e) => { e.stopPropagation(); openAttributionModal(); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Integrate
                    </button>
                  )

              return (
                <div key={item.id} className="library-row" onClick={() => navigate(`/app/content/${item.id}`)}>
                  <div>
                    <div className="library-row-title">{item.title}</div>
                    <div className="library-row-date">Created {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  </div>
                  <div
                    className="library-row-prompt"
                    onClick={(e) => {
                      e.stopPropagation()
                      openTargetPromptModal(item.id)
                    }}
                    title={item.targetQueries.length > 0 ? item.targetQueries.join('\n') : (item.targetQuery || '')}
                    style={{ cursor: 'pointer' }}
                  >
                    {item.targetQueries.length > 0 ? (
                      <div className="target-prompt-list">
                        {(expandedTargetPrompts[item.id] ? item.targetQueries : item.targetQueries.slice(0, 2)).map((q) => (
                          <div key={q} className="target-prompt-item" title={q}>
                            {q}
                          </div>
                        ))}
                        {item.targetQueries.length > 2 && (
                          <button
                            type="button"
                            className="target-prompt-toggle"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setExpandedTargetPrompts((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                            }}
                          >
                            {expandedTargetPrompts[item.id] ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                    ) : (
                      item.targetQuery ? `"${item.targetQuery}"` : (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            openTargetPromptModal(item.id)
                          }}
                        >
                          <svg className="icon" style={{ width: '14px', height: '14px', marginRight: '6px' }}>
                            <use href="#icon-plus"></use>
                          </svg>
                          Add
                        </button>
                      )
                    )}
                  </div>
                  <div className="library-row-platforms">
                    {Object.keys(item.platforms || {}).length === 0 ? (
                      <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                    ) : (
                      Object.entries(item.platforms || {}).map(([p, pd]: any) => {
                        const hasContent = !!(pd?.markdownSource || pd?.content)
                        const publishedUrl = pd?.publishedUrl
                        return (
                          <div key={p} className={`platform-badge-with-url ${hasContent ? 'active' : ''}`}>
                            <svg className="icon" style={{ width: '14px', height: '14px' }}>
                              <use href={`#icon-${p}`}></use>
                            </svg>
                            {publishedUrl ? (
                              <a
                                href={publishedUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="platform-url"
                                onClick={(e) => e.stopPropagation()}
                                title={publishedUrl}
                              >
                                {(() => {
                                  try {
                                    return new URL(publishedUrl).hostname
                                  } catch {
                                    return publishedUrl
                                  }
                                })()}
                              </a>
                            ) : (
                              <span className="platform-name">{p}</span>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                  <div className="library-row-traffic">{trafficDisplay}</div>
                  <div>
                    <span className={`library-row-status ${statusClass}`}>
                      {statusText}
                    </span>
                  </div>
                  <div className="library-row-actions" onClick={(e) => e.stopPropagation()}>
                    {isGenerating ? (
                      <button className="btn btn-secondary btn-sm" disabled>Generating</button>
                    ) : item.status === 'published' ? (
                      <>
                        <button className="btn btn-warning btn-sm" onClick={() => handleUnpublish(item.id)}>
                          Unpublish
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openPublishModal(item.id)}>
                          Links
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-primary btn-sm" onClick={() => handlePublish(item.id)}>
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      <Modal
        show={showTargetPromptModal}
        onClose={() => {
          setShowTargetPromptModal(false)
          setEditingItemId(null)
          setEditingSelectedPromptIds([])
        }}
        title="Select Target Prompts"
        size="lg"
      >
        <p className="modal-desc">Choose which prompts you want this content to target</p>
        {prompts.length === 0 ? (
          <div className="modal-prompt-empty">
            <p>No prompts tracked yet.</p>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/app/prompts')}>
              Go to Prompt Tracking
            </button>
          </div>
        ) : (
          <>
            <div className="modal-prompt-list">
              {prompts.map((p) => {
                const isSelected = editingSelectedPromptIds.includes(p.id)
                return (
                  <div
                    key={p.id}
                    className={`modal-prompt-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      setEditingSelectedPromptIds((prev) => {
                        const next = [...prev]
                        const idx = next.indexOf(p.id)
                        if (idx >= 0) next.splice(idx, 1)
                        else next.push(p.id)
                        return next
                      })
                    }}
                  >
                    <div className="modal-prompt-checkbox">
                      <svg className="icon">
                        <use href="#icon-check"></use>
                      </svg>
                    </div>
                    <div className="modal-prompt-content">
                      <div className="modal-prompt-text">
                        {p.text}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="modal-footer">
              <span className="modal-selected-count">{editingSelectedPromptIds.length} selected</span>
              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingSelectedPromptIds([])}>Clear</button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveTargetPrompts}>Done</button>
              </div>
            </div>
          </>
        )}
      </Modal>

      <Modal
        show={showPublishModal}
        onClose={() => {
          setShowPublishModal(false)
          setPublishingItemId(null)
          setPublishingItem(null)
          setPublishingUrls({})
        }}
        title="Publish Links"
        size="xl"
      >
        {!publishingItem ? (
          <div style={{ padding: '12px' }}>Loading...</div>
        ) : (
          <>
            <p className="modal-desc">
              Copy the content, upload externally, then paste the published link(s) here.
            </p>
            <div className="publish-links-list">
              {Object.entries(publishingItem.platforms || {}).map(([platform, data]: any) => {
                const content = data?.markdownSource || data?.content || ''
                return (
                  <div key={platform} className="publish-platform-card">
                    <div className="publish-platform-header">
                      <div className="publish-platform-title">{platform}</div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigator.clipboard.writeText(content)}
                        disabled={!content}
                      >
                        Copy
                      </button>
                    </div>
                    <div className="publish-platform-body">
                      <LexicalMarkdownEditor
                        initialMarkdown={content}
                        onMarkdownChange={() => {}}
                        placeholder=""
                        readOnly
                        minHeight={180}
                        maxHeight={260}
                      />
                    </div>
                    <div className="publish-platform-footer">
                      <input
                        type="text"
                        className="publish-url-input"
                        placeholder="Paste published URL..."
                        value={publishingUrls[platform] ?? ''}
                        onChange={(e) => setPublishingUrls((prev) => ({ ...prev, [platform]: e.target.value }))}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="modal-footer modal-footer--right">
              <div className="modal-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPublishModal(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSavePublishLinks}>Save</button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Attribution Integration Modal */}
      <AttributionModal
        show={showAttributionModal}
        onClose={() => setShowAttributionModal(false)}
        onConnectedChange={(connected) => setAttributionConnected(connected)}
      />
    </>
  )
}
